'use client';

import {
  ChartNoAxesColumnIncreasing,
  ChartNoAxesCombined,
  Info,
  Percent,
  Target,
  Trophy,
} from 'lucide-react';
import { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import TabBar from '@/components/common/TabBar';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import type {
  WorldCupDataTableData,
  WorldCupDataTableOddsCell,
  WorldCupDataTableRow,
  WorldCupDataTableSection,
} from './types';
import { translateWorldCupTeamText } from '../teamTranslation';

type MarketValueMode = 'odds' | 'percent';

export interface MarketSectionProps {
  section: WorldCupDataTableSection;
  columns?: WorldCupDataTableData['columns'];
  data?: Pick<WorldCupDataTableData, 'columns'>;
}

export default function MarketSection({ section, columns, data }: MarketSectionProps) {
  const { t } = useTranslation();
  const [valueMode, setValueMode] = useState<MarketValueMode>('percent');
  const resolvedColumns = columns ?? data?.columns ?? [];
  const gridTemplateColumns = getGridTemplateColumns(resolvedColumns.length);

  return (
    <section className="overflow-hidden rounded-xl border border-[#262626] bg-black py-3">
      <div className="flex h-8 items-center justify-between gap-3 px-3">
        <div className="flex items-center gap-1">
          <SectionIcon section={section} />
          <h3 className="text-base font-semibold leading-5 text-white">{t(section.title)}</h3>
          {section.tooltip && <SectionTooltip tooltip={section.tooltip} />}
        </div>

        <div className="flex items-center gap-2">
          <div className="text-xs leading-4 text-[#757575]">
            {section.volume} {t('Vol')}
          </div>
          <Tabbar value={valueMode} onChange={setValueMode} />
        </div>
      </div>

      <div className="mt-2">
        <HeaderRow columns={resolvedColumns} gridTemplateColumns={gridTemplateColumns} />
        <div className="divide-y divide-[#171717]">
          {section.rows.map((row) => (
            <DataRow
              key={`${section.title}-${row.label}`}
              row={row}
              gridTemplateColumns={gridTemplateColumns}
              valueMode={valueMode}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionTooltip({ tooltip }: { tooltip?: WorldCupDataTableSection['tooltip'] }) {
  const { t } = useTranslation();
  const icon = <Info className="size-4 text-[#757575]" aria-hidden="true" />;

  if (!tooltip) return icon;
  const title = typeof tooltip === 'string' ? t(tooltip) : tooltip;

  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#1E1E1E',
            border: '1px solid #3A3A3A',
            color: '#fff',
            fontSize: 14,
            lineHeight: '20px',
            maxWidth: 280,
          },
        },
        arrow: {
          sx: {
            color: '#1E1E1E',
          },
        },
      }}
    >
      <span className="inline-flex cursor-help">{icon}</span>
    </Tooltip>
  );
}

function Tabbar({
  value,
  onChange,
}: {
  value: MarketValueMode;
  onChange: (value: MarketValueMode) => void;
}) {
  return (
    <TabBar
      value={value}
      tabItems={[
        {
          value: 'percent',
          label: <Percent className="size-4" aria-hidden="true" />,
        },
        {
          value: 'odds',
          label: <ChartNoAxesColumnIncreasing className="size-3.5" aria-hidden="true" />,
        },
      ]}
      onChange={(nextValue) => onChange(nextValue as MarketValueMode)}
      warpperClassName="h-6 w-[57px] gap-0 rounded-lg bg-[#171717] p-px"
      tabItemClassName="h-[22px] w-7 rounded-lg p-0 text-[#757575]"
      activeClassName="bg-[#363636] text-white"
    />
  );
}

function HeaderRow({
  columns,
  gridTemplateColumns,
}: {
  columns: WorldCupDataTableData['columns'];
  gridTemplateColumns: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="border-y border-[#262626] bg-[#1E1E1E]">
      <div className="grid h-9 items-center gap-0 px-3" style={{ gridTemplateColumns }}>
        <div className="flex items-center justify-between pr-4 text-sm leading-4">
          <span className="text-[#B7B9BC]">{t('Sub Market')}</span>
        </div>
        {columns.map((column) => (
          <div
            key={column.id}
            className={cn(
              'flex h-4 items-center justify-center text-sm font-semibold leading-4 text-white',
              column.accent_class_name,
            )}
          >
            {column.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function DataRow({
  row,
  gridTemplateColumns,
  valueMode,
}: {
  row: WorldCupDataTableRow;
  gridTemplateColumns: string;
  valueMode: MarketValueMode;
}) {
  const hasRowDetail = Boolean(row.secondary_label || row.volume);

  return (
    <div
      className={cn('grid items-center px-3', hasRowDetail ? 'min-h-12 py-1' : 'h-12')}
      style={{ gridTemplateColumns }}
    >
      <div className="flex h-10 gap-3 justify-between items-center rounded-xl pr-4">
        <MarketLabel row={row} />
        {row.probability && (
          <span
            className={cn(
              'shrink-0 text-sm font-semibold leading-4 tabular-nums',
              row.probability_class_name ?? 'text-[#14C784]',
            )}
          >
            {row.probability}
          </span>
        )}
      </div>
      {row.odds.map((odd, index) => (
        <OddsCell key={`${row.label}-${index}`} odd={odd} valueMode={valueMode} />
      ))}
    </div>
  );
}

function MarketLabel({ row }: { row: WorldCupDataTableRow }) {
  const { t } = useTranslation();
  const label = getTranslatedMarketLabel(row.label, t);
  const secondaryLabel = row.secondary_label
    ? getTranslatedMarketLabel(row.secondary_label, t)
    : undefined;
  const volume = row.volume ? `${row.volume} ${t('Vol')}` : undefined;
  const detailLabel = [secondaryLabel, volume].filter(Boolean).join(' / ');

  if (!detailLabel) {
    if (row.badge_class_name) {
      return (
        <span className={cn('truncate text-sm font-semibold leading-4', row.badge_class_name)}>
          {label}
        </span>
      );
    }

    if (['yes', 'no'].includes(row.label.toLowerCase())) {
      return (
        <span
          className={cn(
            'truncate text-sm font-semibold leading-4 px-2 py-0.5 rounded',
            row.probability_class_name,
            'yes' === row.label.toLowerCase() ? 'bg-[#14C784]/15' : 'bg-[#FF2D2D]/15',
          )}
        >
          {label}
        </span>
      );
    }

    return (
      <span className={cn('truncate text-sm font-semibold leading-4 text-white')}>{label}</span>
    );
  }

  if (row.badge_class_name) {
    return (
      <span className="flex min-w-0 flex-col gap-1">
        <span className={cn('truncate text-sm font-semibold leading-4', row.badge_class_name)}>
          {label}
        </span>
        {detailLabel && (
          <span className="truncate text-xs font-normal leading-3.5 text-[#757575]">
            {detailLabel}
          </span>
        )}
      </span>
    );
  }

  if (['yes', 'no'].includes(row.label.toLowerCase())) {
    return (
      <span className="flex min-w-0 flex-col gap-1">
        <span
          className={cn(
            'w-fit max-w-full truncate text-sm font-semibold leading-4 px-2 py-0.5 rounded',
            row.probability_class_name,
            'yes' === row.label.toLowerCase() ? 'bg-[#14C784]/15' : 'bg-[#FF2D2D]/15',
          )}
        >
          {label}
        </span>
        {detailLabel && (
          <span className="truncate text-xs font-normal leading-3.5 text-[#757575]">
            {detailLabel}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="flex min-w-0 flex-col gap-1">
      <span className={cn('truncate text-sm font-semibold leading-4 text-white')}>{label}</span>
      {detailLabel && (
        <span className="truncate text-xs font-normal leading-3.5 text-[#757575]">
          {detailLabel}
        </span>
      )}
    </span>
  );
}

function OddsCell({
  odd,
  valueMode,
}: {
  odd: WorldCupDataTableOddsCell;
  valueMode: MarketValueMode;
}) {
  const value = valueMode === 'percent' ? (odd.percent_value ?? '-') : odd.value;

  return (
    <div className="flex h-10 items-center justify-center rounded-xl text-sm font-medium leading-4 text-[#B7B9BC] tabular-nums">
      {value}
    </div>
  );
}

function SectionIcon({ section }: { section: WorldCupDataTableSection }) {
  const className = cn('size-4', section.icon_class_name);

  if (section.icon_name === 'spreads') {
    return <Percent className={className} aria-hidden="true" />;
  }

  if (section.icon_name === 'totals') {
    return <Target className={className} aria-hidden="true" />;
  }

  if (section.icon_name === 'exact-score') {
    return <ChartNoAxesCombined className={className} aria-hidden="true" />;
  }

  return <Trophy className={className} aria-hidden="true" />;
}

function getGridTemplateColumns(columnCount: number) {
  return `minmax(240px,1.6fr) repeat(${columnCount}, minmax(132px,1fr))`;
}

function getTranslatedMarketLabel(label: string, t: (key: string) => string) {
  const normalizedLabel = label.trim().toLowerCase();
  if (['yes', 'no', 'draw', 'over', 'under'].includes(normalizedLabel)) {
    return t(label);
  }

  if (normalizedLabel.endsWith(' win')) {
    const teamName = label.slice(0, -4).trim();
    return `${translateWorldCupTeamText(teamName, t)} ${t('Win')}`;
  }

  return translateWorldCupTeamText(label, t);
}
